/**
 * @file ProductReviewsFilters.tsx
 * @description Чипы-вкладки по статусам и панель фильтров очереди модерации
 * отзывов (phase19.10). Mobile-first: чипы горизонтально скроллятся,
 * поля фильтров переносятся.
 */

import { EditorialCard } from "../../../components/ui/EditorialCard";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import type {
  ProductReviewStatus,
  ReviewCounts,
} from "../../../lib/api-product-reviews";
import { cn } from "@/lib/utils";

const STATUS_TABS: ProductReviewStatus[] = [
  "PENDING",
  "PUBLISHED",
  "REJECTED",
  "HIDDEN",
];

/** Подписи вкладок отличаются от подписи статуса одного отзыва. */
const TAB_LABEL: Record<ProductReviewStatus, string> = {
  PENDING: "На проверке",
  PUBLISHED: "Опубликованные",
  REJECTED: "Отклонённые",
  HIDDEN: "Скрытые",
};

export function ReviewStatusTabs({
  value,
  counts,
  onChange,
}: {
  value: ProductReviewStatus;
  counts: ReviewCounts | null;
  onChange: (status: ProductReviewStatus) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Фильтр по статусу отзыва"
      className="-mb-1 flex items-center gap-1.5 overflow-x-auto pb-1 md:mb-0 md:flex-wrap md:overflow-visible md:pb-0"
    >
      {STATUS_TABS.map((s) => {
        const active = s === value;
        return (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(s)}
            className={cn(
              "inline-flex h-11 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 text-sm md:h-9 md:px-3",
              "transition-colors duration-200",
              active
                ? "bg-primary font-medium text-on-primary"
                : "bg-surface-low text-on-surface-variant hover:bg-accent",
            )}
          >
            {TAB_LABEL[s]}
            <span
              className={cn(
                "text-xs tabular-nums",
                active ? "text-on-primary/80" : "text-on-surface-muted",
              )}
            >
              {counts?.[s] ?? 0}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export interface ReviewFiltersValue {
  search: string;
  productId: string;
  rating: string;
  withPhotos: boolean;
}

interface ReviewFiltersProps {
  value: ReviewFiltersValue;
  /** Список товаров для селекта; пустой — селект скрыт (нет прав/не загрузился). */
  products: { id: string; displayName: string }[];
  onChange: (patch: Partial<ReviewFiltersValue>) => void;
  onSearchSubmit: () => void;
}

export function ReviewFilters({
  value,
  products,
  onChange,
  onSearchSubmit,
}: ReviewFiltersProps) {
  return (
    <EditorialCard>
      <EditorialCard.Body>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex min-w-60 flex-1 flex-col gap-1.5">
            <Label htmlFor="rev-search">Поиск по тексту или автору</Label>
            <Input
              id="rev-search"
              value={value.search}
              onChange={(e) => onChange({ search: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSearchSubmit();
              }}
              placeholder="фрагмент отзыва или имя"
            />
          </div>

          {products.length > 0 ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rev-product">Товар</Label>
              <select
                id="rev-product"
                value={value.productId}
                onChange={(e) => onChange({ productId: e.target.value })}
                className="h-11 max-w-60 rounded-md bg-input px-3 text-sm md:h-10"
              >
                <option value="">Все товары</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.displayName}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rev-rating">Оценка</Label>
            <select
              id="rev-rating"
              value={value.rating}
              onChange={(e) => onChange({ rating: e.target.value })}
              className="h-11 rounded-md bg-input px-3 text-sm md:h-10"
            >
              <option value="">Любая</option>
              {[5, 4, 3, 2, 1].map((r) => (
                <option key={r} value={String(r)}>
                  {r} ★
                </option>
              ))}
            </select>
          </div>

          <label className="flex h-11 cursor-pointer items-center gap-2 text-sm text-on-surface-variant md:h-10">
            <input
              type="checkbox"
              checked={value.withPhotos}
              onChange={(e) => onChange({ withPhotos: e.target.checked })}
              className="size-4 accent-primary"
            />
            Только с фото
          </label>
        </div>
      </EditorialCard.Body>
    </EditorialCard>
  );
}
